import { useEffect } from 'react'
import { Box } from '@mui/material'
import { Outlet, useLocation, useNavigationType } from 'react-router-dom'
import { layout } from '@/theme'
import { Header } from './Header'
import { Footer } from './Footer'
import { WhatsAppFab } from './WhatsAppFab'
import { GrainOverlay } from '@/components/fx/GrainOverlay'
import { CustomCursor } from '@/components/fx/CustomCursor'

// Scroll position per history entry (survives SPA re-renders; cleared on full reload).
const scrollPositions = new Map<string, number>()

/**
 * App chrome: fixed Header + main + Footer + WhatsApp FAB. The hero on Home sits
 * under the transparent header (no offset); every other route gets a top offset
 * equal to the header height so content clears the fixed bar.
 */
export function Layout() {
  const location = useLocation()
  const navType = useNavigationType()
  const isHome = location.pathname === '/'

  // Take manual control so the browser's own restore doesn't fight ours.
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'
  }, [])

  // Record the scroll position for the current entry as the user scrolls.
  useEffect(() => {
    const key = location.key
    const save = () => scrollPositions.set(key, window.scrollY)
    window.addEventListener('scroll', save, { passive: true })
    return () => {
      save() // capture final position before leaving this entry
      window.removeEventListener('scroll', save)
    }
  }, [location.key])

  // New navigations (PUSH/REPLACE) start at the top; back/forward (POP) restores.
  useEffect(() => {
    if (navType === 'POP') {
      window.scrollTo(0, scrollPositions.get(location.key) ?? 0)
    } else {
      window.scrollTo(0, 0)
    }
  }, [location.key, navType])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      <Header />
      <Box component="main" sx={{ flex: 1, pt: isHome ? 0 : { xs: '60px', md: `${layout.headerHeight}px` } }}>
        <Outlet />
      </Box>
      <Footer />
      <WhatsAppFab />
      <GrainOverlay />
      <CustomCursor />
    </Box>
  )
}
